import React, { useEffect, useState } from "react";
import {
  GitBranch,
  Plus,
  Trash2,
  RefreshCw,
  Server,
  Smartphone,
  Monitor,
  ChevronRight,
  Network,
  Loader2,
  Settings2,
} from "lucide-react";
import Button from "../../../components/forms/Button";
import { qaApi, RISK_META, RECOMMENDATION_META, formatTime } from "./api";

const REPO_TYPE_ICON = { backend: Server, frontend: Monitor, mobile: Smartphone };

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-white dark:bg-dark-400 border border-gray-200 dark:border-dark-100 text-sm text-gray-900 dark:text-light-100";

const Labeled = ({ label, children }) => (
  <div>
    <label className="block text-xs text-gray-500 dark:text-light-500 mb-1">{label}</label>
    {children}
  </div>
);

const RiskBadge = ({ level }) => {
  const meta = RISK_META[level];
  if (!meta) return <span className="text-xs text-gray-400">—</span>;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

const Overview = ({ onSelectCommit }) => {
  const [repos, setRepos] = useState([]);
  const [commits, setCommits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ organization: "", name: "", repoType: "backend" });
  const [saving, setSaving] = useState(false);
  const [indexing, setIndexing] = useState({});
  const [cfgRepoId, setCfgRepoId] = useState(null);
  const [cfg, setCfg] = useState({});
  const [savingCfg, setSavingCfg] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([qaApi.listRepositories(), qaApi.listCommits({ limit: 30 })]);
      setRepos(r.repositories || []);
      setCommits(c.commits || []);
    } catch (e) {
      // surface nothing destructive; empty state will show
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAddRepo = async () => {
    if (!form.organization || !form.name) return;
    setSaving(true);
    try {
      await qaApi.createRepository(form);
      setForm({ organization: "", name: "", repoType: "backend" });
      setShowAdd(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRepo = async (id) => {
    await qaApi.deleteRepository(id);
    await load();
  };

  const openConfig = (repo) => {
    setCfgRepoId(repo._id);
    setCfg({
      baseUrl: repo.testConfig?.baseUrl || "",
      loginEndpoint: repo.testConfig?.loginEndpoint || "/auth/login",
      usernameField: repo.testConfig?.usernameField || "email",
      username: repo.testConfig?.username || "",
      password: "",
      tokenPath: repo.testConfig?.tokenPath || "",
      isStaging: repo.testConfig?.isStaging || false,
    });
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const payload = { ...cfg };
      if (!payload.password) delete payload.password; // don't overwrite stored password with blank
      await qaApi.updateTestConfig(cfgRepoId, payload);
      setCfgRepoId(null);
      await load();
    } finally {
      setSavingCfg(false);
    }
  };

  const handleIndexRepo = async (id) => {
    setIndexing((s) => ({ ...s, [id]: true }));
    try {
      await qaApi.indexRepository(id);
      // Indexing runs in the background on the server; reflect "started" briefly.
      setTimeout(() => setIndexing((s) => ({ ...s, [id]: false })), 4000);
    } catch {
      setIndexing((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Connected repositories */}
      <section className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-light-100 flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-primary-accent" /> Connected Repositories
          </h3>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" icon={<RefreshCw className="w-4 h-4" />} onClick={load} />
            <Button variant="primary" size="sm" icon={<Plus className="w-4 h-4" />} onClick={() => setShowAdd((v) => !v)}>
              Add Repo
            </Button>
          </div>
        </div>

        {showAdd && (
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-4 gap-2 p-3 rounded-xl bg-gray-50 dark:bg-dark-400">
            <input
              className="px-3 py-2 rounded-lg bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 text-sm"
              placeholder="organization"
              value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}
            />
            <input
              className="px-3 py-2 rounded-lg bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 text-sm"
              placeholder="repo name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <select
              className="px-3 py-2 rounded-lg bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 text-sm"
              value={form.repoType}
              onChange={(e) => setForm({ ...form, repoType: e.target.value })}
            >
              <option value="backend">backend</option>
              <option value="frontend">frontend</option>
              <option value="mobile">mobile</option>
            </select>
            <Button variant="primary" size="sm" onClick={handleAddRepo} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        )}

        {repos.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-light-500">
            No repositories connected yet. Add one, then point its GitHub webhook to{" "}
            <code className="text-xs">/api/ai-studio/qa-engineer/webhooks/github</code>.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {repos.map((repo) => {
              const Icon = REPO_TYPE_ICON[repo.repoType] || Server;
              return (
                <div
                  key={repo._id}
                  className="flex items-center justify-between p-3 rounded-xl border border-gray-200 dark:border-dark-100 bg-gray-50 dark:bg-dark-400"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-4 h-4 text-primary-accent shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-light-100 truncate">
                        {repo.organization}/{repo.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-light-500">{repo.repoType}</p>
                      {repo.repoType !== "backend" && (
                        <p className="text-[10px] text-gray-400 dark:text-light-500 mt-0.5">
                          Runtime tests run on backend repos
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {/* Runtime tests hit HTTP endpoints, so test config only
                        applies to backend repos. Frontend/mobile repos are
                        covered via impact analysis on the endpoints they call. */}
                    {repo.repoType === "backend" && (
                      <button
                        className="text-gray-400 hover:text-primary-accent transition"
                        onClick={() => openConfig(repo)}
                        title="Test configuration (auth / base URL)"
                      >
                        <Settings2 className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      className="text-gray-400 hover:text-primary-accent transition disabled:opacity-50"
                      onClick={() => handleIndexRepo(repo._id)}
                      disabled={indexing[repo._id]}
                      title="Index repository (build dependency graph)"
                    >
                      {indexing[repo._id] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Network className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      className="text-gray-400 hover:text-red-500 transition"
                      onClick={() => handleDeleteRepo(repo._id)}
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Test configuration panel */}
      {cfgRepoId && (
        <section className="bg-white dark:bg-dark-300 border border-primary-accent/40 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-light-100 flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-primary-accent" /> Test Configuration
            </h3>
            <button className="text-xs text-gray-400 hover:text-gray-700 dark:hover:text-light-200" onClick={() => setCfgRepoId(null)}>
              Cancel
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-light-500 mb-3">
            The engine logs in with these credentials to get a fresh token before running tests. Use a
            <strong> staging</strong> base URL — write scenarios only run when “Staging target” is on.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Labeled label="Base URL">
              <input className={inputCls} placeholder="https://staging.example.com/api" value={cfg.baseUrl} onChange={(e) => setCfg({ ...cfg, baseUrl: e.target.value })} />
            </Labeled>
            <Labeled label="Login endpoint">
              <input className={inputCls} placeholder="/auth/login" value={cfg.loginEndpoint} onChange={(e) => setCfg({ ...cfg, loginEndpoint: e.target.value })} />
            </Labeled>
            <Labeled label="Identifier field">
              <select className={inputCls} value={cfg.usernameField} onChange={(e) => setCfg({ ...cfg, usernameField: e.target.value })}>
                <option value="email">email</option>
                <option value="username">username</option>
                <option value="mobile">mobile</option>
              </select>
            </Labeled>
            <Labeled label="Username / email / mobile">
              <input className={inputCls} value={cfg.username} onChange={(e) => setCfg({ ...cfg, username: e.target.value })} />
            </Labeled>
            <Labeled label="Password">
              <input type="password" className={inputCls} placeholder="(unchanged)" value={cfg.password} onChange={(e) => setCfg({ ...cfg, password: e.target.value })} />
            </Labeled>
            <Labeled label="Token path (optional)">
              <input className={inputCls} placeholder="data.token" value={cfg.tokenPath} onChange={(e) => setCfg({ ...cfg, tokenPath: e.target.value })} />
            </Labeled>
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 dark:text-light-300">
            <input type="checkbox" checked={cfg.isStaging} onChange={(e) => setCfg({ ...cfg, isStaging: e.target.checked })} />
            Staging target (allow write scenarios — Create/Update)
          </label>
          <div className="mt-4">
            <Button variant="primary" size="sm" onClick={saveConfig} disabled={savingCfg}>
              {savingCfg ? "Saving…" : "Save configuration"}
            </Button>
          </div>
        </section>
      )}

      {/* Risk heatmap */}
      <section className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl p-5">
        <h3 className="text-base font-semibold text-gray-900 dark:text-light-100 mb-3">Risk Heatmap</h3>
        {commits.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-light-500">No analyzed commits yet.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {commits.map((c) => {
              const meta = RISK_META[c.risk?.level] || { dot: "bg-gray-300" };
              return (
                <button
                  key={c._id}
                  title={`${c.repo?.name || ""} ${c.sha?.slice(0, 7)} — ${c.risk?.level || "pending"}`}
                  className={`w-6 h-6 rounded ${meta.dot} hover:ring-2 ring-primary-accent transition`}
                  onClick={() => onSelectCommit?.(c._id)}
                />
              );
            })}
          </div>
        )}
      </section>

      {/* Latest commits */}
      <section className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-dark-100 rounded-2xl p-5">
        <h3 className="text-base font-semibold text-gray-900 dark:text-light-100 mb-3">Latest Commits</h3>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-light-500">Loading…</p>
        ) : commits.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-light-500">No commits analyzed yet.</p>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-dark-100">
            {commits.map((c) => (
              <button
                key={c._id}
                onClick={() => onSelectCommit?.(c._id)}
                className="w-full flex items-center justify-between py-3 text-left hover:bg-gray-50 dark:hover:bg-dark-400 px-2 rounded-lg transition"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-light-100 truncate">
                    {c.message?.split("\n")[0] || c.sha?.slice(0, 7)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-light-500">
                    {c.repo ? `${c.repo.name} · ` : ""}
                    {c.sha?.slice(0, 7)} · {c.author || "unknown"} · {formatTime(c.createdAt)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.recommendation && RECOMMENDATION_META[c.recommendation] && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RECOMMENDATION_META[c.recommendation].className}`}>
                      {RECOMMENDATION_META[c.recommendation].label}
                    </span>
                  )}
                  <RiskBadge level={c.risk?.level} />
                  {c.risk?.score != null && (
                    <span className="text-xs font-mono text-gray-500 dark:text-light-500">{c.risk.score}</span>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Overview;
